/**
 * BestOddsCard - Single market anomaly card for the subscriber-facing
 * "Best Odds Радар" grid.
 *
 * Unlike MaskedBestOddsPreview's cards (public, masked), this card always
 * shows full, unmasked data and opens BestOddsDetailModal ("odds intelligence")
 * on click/Enter.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from '../../i18n/hooks';
import { getMarginTypeName, getOutcomeName, getBookmakerName } from '../../surebets/utils/surebetUtils';
import NumberUtils from '../../utils/NumberUtils';
import './BestOddsCard.css';

const BestOddsCard = ({ anomaly, onClick }) => {
  const { t, language } = useTranslation();

  const marketLabel = getMarginTypeName(anomaly.marketKey, language);
  const outcomeLabel = getOutcomeName(anomaly.marketKey, anomaly.outcomeType, language);

  const handleActivate = () => {
    if (onClick) {
      onClick(anomaly);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleActivate();
    }
  };

  return (
    <article
      className="best-odds-card"
      role="button"
      tabIndex={0}
      aria-label={`${t('bestOdds.card.ariaLabel', 'Best Odds сигнал')}: ${anomaly.event.teamOne} vs ${anomaly.event.teamTwo}`}
      onClick={handleActivate}
      onKeyDown={handleKeyDown}
    >
      <div className="best-odds-card__event">
        <div className="best-odds-card__teams">
          <span className="team-name">{anomaly.event.teamOne}</span>
          <span className="vs">vs</span>
          <span className="team-name">{anomaly.event.teamTwo}</span>
        </div>
        <div className="best-odds-card__meta">
          <span>{anomaly.event.date}</span>
          <span>{anomaly.event.time}</span>
          <span>{anomaly.event.championship}</span>
        </div>
      </div>

      <div className="best-odds-card__market">{marketLabel}</div>

      <div className="best-odds-card__best-odds">
        <span className="best-odds-card__badge">
          🥇 {t('bestOdds.card.newBestOddsBadge', 'НОВ НАЙ-ДОБЪР КОЕФИЦИЕНТ')}
        </span>
        <div className="best-odds-card__best-odds-row">
          <span className="best-odds-card__outcome">{outcomeLabel}</span>
          <span className="best-odds-card__odds-value">
            {NumberUtils.formatOdds(anomaly.bestOdds, { decimals: 2 })}
          </span>
          <span className="best-odds-card__bookmaker">{getBookmakerName(anomaly.bestBookmaker)}</span>
        </div>
      </div>

      <div className="best-odds-card__stats">
        <div className="best-odds-card__stat">
          <span className="best-odds-card__stat-label">
            {t('bestOdds.card.marketAvgLabel', 'Пазарна средна')}
          </span>
          <span className="best-odds-card__stat-value">
            {NumberUtils.formatOdds(anomaly.marketAvg, { decimals: 2 })}
          </span>
        </div>
        <div className="best-odds-card__stat best-odds-card__stat--spread">
          <span className="best-odds-card__stat-label">
            {t('bestOdds.card.spreadLabel', 'Отклонение')}
          </span>
          <span className="best-odds-card__stat-value best-odds-card__stat-value--positive">
            +{NumberUtils.formatPercentage(anomaly.spread, { decimals: 1, showSymbol: true })}
          </span>
        </div>
        <div className="best-odds-card__stat best-odds-card__stat--count">
          <span className="best-odds-card__stat-label">
            {t('bestOdds.card.bookmakerCountLabel', 'Букмейкъри')}
          </span>
          <span className="best-odds-card__stat-value">{anomaly.bookmakerCount}</span>
        </div>
      </div>

      <button
        type="button"
        className="best-odds-card__view-btn"
        onClick={(event) => {
          event.stopPropagation();
          handleActivate();
        }}
      >
        👁 {t('bestOdds.card.viewButton', 'Преглед')}
      </button>
    </article>
  );
};

BestOddsCard.propTypes = {
  anomaly: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    event: PropTypes.shape({
      teamOne: PropTypes.string,
      teamTwo: PropTypes.string,
      date: PropTypes.string,
      time: PropTypes.string,
      championship: PropTypes.string
    }).isRequired,
    marketKey: PropTypes.string.isRequired,
    outcomeType: PropTypes.string.isRequired,
    bestBookmaker: PropTypes.string.isRequired,
    bestOdds: PropTypes.number.isRequired,
    marketAvg: PropTypes.number.isRequired,
    spread: PropTypes.number.isRequired,
    bookmakerCount: PropTypes.number
  }).isRequired,
  onClick: PropTypes.func
};

export default BestOddsCard;
